import { useEffect } from 'react';

import { ImageContainer, isSSR, XemploCdn } from '@xemplo/common-util';
import { safeCallWithSentryAsync } from '@xemplo/safe-call';

// CDN URL for the flag sprite
const SPRITE_URL = XemploCdn.Images(ImageContainer.svg, 'sprite-flag.min.svg');
// A fixed container id to make insertion idempotent and discoverable
const SPRITE_CONTAINER_ID = 'xemplo-flag-sprite-container';

// Module-scoped state to ensure the sprite is fetched and injected only once
let loaded = false;
let loadPromise: Promise<void> | null = null;

/**
 * Checks if the flags sprite container is present in the DOM.
 * @returns {boolean} True if the sprite container element exists, false otherwise.
 */
function isSpritePresentInDom(): boolean {
  if (isSSR()) return false;
  return document.getElementById(SPRITE_CONTAINER_ID) != null;
}

/**
 * Fetches the flag sprite from the Xemplo CDN and returns its content as a string.
 * This function wraps the fetch operation in a safe call with Sentry error reporting.
 * @returns {Promise<string>} A promise that resolves to the sprite content as a string.
 * @see {@link safeCallWithSentryAsync}
 */
async function fetchSprite() {
  return await safeCallWithSentryAsync(
    fetch(SPRITE_URL, { credentials: 'omit' }).then(async (res) => {
      if (!res.ok) {
        throw new Error(`Failed to load flag sprite: ${res.status} ${res.statusText}`);
      }
      return res.text();
    }),
    {
      tags: { feature: 'flag-sprite-fetch' },
      extra: { spriteUrl: SPRITE_URL },
    },
  );
}

/**
 * Injects an SVG sprite into the DOM if the DOM is available and the sprite is not already present.
 * Creates a hidden container div with the provided SVG sprite content, styles it to be invisible,
 * and appends it to the document body. The root SVG element is also hidden to prevent layout issues.
 * @param svgSprite - The SVG sprite string to inject into the DOM.
 */
function injectSprite(svgSprite: string) {
  if (isSSR()) return;

  // If already injected, do nothing
  if (isSpritePresentInDom()) return;

  const container = document.createElement('div');
  container.id = SPRITE_CONTAINER_ID;
  container.style.position = 'absolute';
  container.style.width = '0';
  container.style.height = '0';
  container.style.overflow = 'hidden';
  container.style.pointerEvents = 'none';
  container.style.visibility = 'hidden';
  container.setAttribute('aria-hidden', 'true');

  // We trust the CDN content.
  container.innerHTML = svgSprite;

  // Hide the root <svg> to avoid layout/visibility issues if styles differ
  const svg = container.querySelector('svg');
  if (svg) {
    (svg as SVGElement).style.display = 'none';
    svg.setAttribute('aria-hidden', 'true');
  }

  document.body.appendChild(container);
}

/**
 * Ensures that the flag sprite is loaded into the DOM.
 *
 * This function checks if the sprite is already loaded or present in the DOM.
 * If not, it fetches the sprite from the Xemplo CDN, injects it into the DOM,
 * and verifies that it was successfully added. It handles concurrent calls by
 * returning the existing load promise if one is in progress.
 *
 * @returns A promise that resolves when the sprite is ensured to be loaded.
 */
export function ensureFlagSprite(): Promise<void> {
  // If DOM is not available (SSR), do nothing and resolve.
  if (isSSR()) return Promise.resolve();

  if (loaded) return Promise.resolve();

  // If present in DOM already (e.g., injected at app bootstrap), mark as loaded
  if (isSpritePresentInDom()) {
    loaded = true;
    return Promise.resolve();
  }

  if (loadPromise) return loadPromise;

  loadPromise = (async () => {
    try {
      const [svgSprite] = await fetchSprite();
      if (!svgSprite || !svgSprite.trim()) return;

      injectSprite(svgSprite);

      // Only mark loaded if it actually ended up in the DOM
      loaded = isSpritePresentInDom();
    } finally {
      // Clear the promise so that future calls can retry if needed
      loadPromise = null;
    }
  })();

  return loadPromise;
}

/**
 * Little utility to reset the loaded state for tests.
 * DO NOT USE IN REAL IMPLEMENTATIONS.
 */
export function __resetFlagSpriteForTests() {
  if (process.env.NODE_ENV !== 'test') {
    throw new Error('__resetFlagSpriteForTests should only be used in tests');
  }

  loaded = false;
  loadPromise = null;
}

/**
 * A lightweight React component that ensures the flag sprite is loaded.
 *
 * This component uses `useEffect` to call `ensureFlagSprite` when it mounts,
 * ensuring that the necessary flag assets are available. It renders nothing
 * (returns `null`) as its primary purpose is side-effect management.
 */
export function FlagsSprite() {
  useEffect(() => {
    ensureFlagSprite();
  }, []);
  return null;
}
